import React, { createContext, useContext, useReducer, useCallback } from 'react';
import apiService from '../services/ApiService';
import { t as translate } from '../i18n/locales';

// Типы действий для reducer
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_THEME: 'SET_THEME',
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_SUBSCRIPTIONS: 'SET_SUBSCRIPTIONS',
  SET_OPERATIONS: 'SET_OPERATIONS',
  SET_SERVERS: 'SET_SERVERS',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_TELEGRAM_AVAILABLE: 'SET_TELEGRAM_AVAILABLE',
  SET_TELEGRAM_THEME: 'SET_TELEGRAM_THEME',
  UPDATE_SUBSCRIPTION: 'UPDATE_SUBSCRIPTION',
  ADD_OPERATION: 'ADD_OPERATION',
  ADD_SUBSCRIPTION: 'ADD_SUBSCRIPTION',
  DELETE_SUBSCRIPTION: 'DELETE_SUBSCRIPTION'
};

// Начальное состояние
const initialState = {
  // Пользователь
  user: null,
  isAuthenticated: false,
  
  // Данные
  subscriptions: [],
  operations: [],
  servers: [],
  
  // UI состояние
  isLoading: true,
  error: null,
  currentTheme: 'dark',
  language: 'ru',  // Язык интерфейса
  
  // Telegram
  isTelegramAvailable: false,
  telegramTheme: {},
  
  // Дополнительные флаги загрузки
  loadingStates: {
    user: false,
    subscriptions: false,
    operations: false,
    servers: false,
    purchase: false
  }
};

// Reducer для управления состоянием
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload.isLoading,
        loadingStates: {
          ...state.loadingStates,
          ...action.payload.loadingStates
        }
      };

    case ActionTypes.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loadingStates: {
          ...state.loadingStates,
          user: false
        }
      };

    case ActionTypes.SET_THEME:
      return {
        ...state,
        currentTheme: action.payload
      };

    case ActionTypes.SET_LANGUAGE:
      return {
        ...state,
        language: action.payload
      };

    case ActionTypes.SET_SUBSCRIPTIONS:
      return {
        ...state,
        subscriptions: action.payload,
        loadingStates: {
          ...state.loadingStates,
          subscriptions: false
        }
      };

    case ActionTypes.SET_OPERATIONS:
      return {
        ...state,
        operations: action.payload,
        loadingStates: {
          ...state.loadingStates,
          operations: false
        }
      };

    case ActionTypes.SET_SERVERS:
      return {
        ...state,
        servers: action.payload,
        loadingStates: {
          ...state.loadingStates,
          servers: false
        }
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        loadingStates: initialState.loadingStates
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case ActionTypes.SET_TELEGRAM_AVAILABLE:
      return {
        ...state,
        isTelegramAvailable: action.payload
      };

    case ActionTypes.SET_TELEGRAM_THEME:
      return {
        ...state,
        telegramTheme: action.payload
      };

    case ActionTypes.UPDATE_SUBSCRIPTION:
      return {
        ...state,
        subscriptions: state.subscriptions.map(sub =>
          sub.id === action.payload.id ? { ...sub, ...action.payload } : sub
        )
      };

    case ActionTypes.ADD_OPERATION:
      return {
        ...state,
        operations: [action.payload, ...state.operations]
      };

    case ActionTypes.ADD_SUBSCRIPTION:
      return {
        ...state,
        subscriptions: [action.payload, ...state.subscriptions]
      };

    case ActionTypes.DELETE_SUBSCRIPTION:
      return {
        ...state,
        subscriptions: state.subscriptions.filter(sub => sub.id !== action.payload)
      };

    default:
      return state;
  }
};

// Создаем контекст
const AppContext = createContext();

// Провайдер контекста
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Функция для установки состояния загрузки
  const setLoading = useCallback((isLoading, loadingStates = {}) => {
    dispatch({
      type: ActionTypes.SET_LOADING,
      payload: { isLoading, loadingStates }
    });
  }, [dispatch]);

  // Функция для валидации и установки пользователя
  const validateAndSetUser = async (userData, fallbackTheme = null) => {
    try {
      setLoading(true, { user: true });
      
      const validationResponse = await apiService.validateUser(userData);
      
      // Backend возвращает UserValidationResponse, а не сам объект пользователя
      // Если валидация успешна, используем исходные данные пользователя
      console.log('validationResponse', validationResponse);
      if (validationResponse.status === 200) {
        const userPayload = {
          ...userData,
          id: userData.user_id,
          is_admin: validationResponse.is_admin,
          balance: validationResponse.balance || 0,  // Добавляем баланс из ответа
          referal_link: validationResponse.referal_link || null  // Добавляем реферальную ссылку
        };
        
        dispatch({
          type: ActionTypes.SET_USER,
          payload: userPayload,
        });
        
        // Применяем тему пользователя из ответа сервера
        if (validationResponse.theme_mode) {
          dispatch({
            type: ActionTypes.SET_THEME,
            payload: validationResponse.theme_mode
          });
        } else if (fallbackTheme) {
          // Если у пользователя нет сохраненной темы, применяем fallback тему (например, из Telegram)
          dispatch({
            type: ActionTypes.SET_THEME,
            payload: fallbackTheme
          });
        }
        // Если нет ни сохраненной темы, ни fallback темы, используется тема по умолчанию из initialState
        
        // Применяем язык пользователя из ответа сервера
        if (validationResponse.language) {
          dispatch({
            type: ActionTypes.SET_LANGUAGE,
            payload: validationResponse.language
          });
        }
        
        return userPayload;
      } else {
        throw new Error(`Ошибка валидации: ${validationResponse.message}`);
      }
    } catch (error) {
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: `Ошибка валидации пользователя: ${error.message}`
      });
      throw error;
    } finally {
      setLoading(false, { user: false });
    }
  };

  // Функция для загрузки подписок пользователя
  const loadUserSubscriptions = async (userId) => {
    try {
      setLoading(true, { subscriptions: true });
      
      console.log('Загрузка подписок для пользователя:', userId);
      
      const response = await apiService.getUserSubscriptions(userId);
      console.log('Ответ от сервера:', response);
      
      // Извлекаем массив подписок из ответа сервера
      const subscriptions = response.subscriptions || [];
      console.log('Обработанные подписки:', subscriptions);
      
      dispatch({
        type: ActionTypes.SET_SUBSCRIPTIONS,
        payload: subscriptions
      });

      return subscriptions;
    } catch (error) {
      console.error('Ошибка загрузки подписок:', error);
      
      // Устанавливаем пустой массив в случае ошибки
      dispatch({
        type: ActionTypes.SET_SUBSCRIPTIONS,
        payload: []
      });
      
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: `Ошибка загрузки подписок: ${error.message}`
      });
      
      // Не бросаем ошибку, чтобы приложение продолжило работу
      return [];
    } finally {
      setLoading(false, { subscriptions: false });
    }
  };

  // Функция для загрузки операций пользователя
  const loadUserOperations = async (userId) => {
    try {
      setLoading(true, { operations: true });
      
      const operations = await apiService.getUserOperations(userId);
      
      dispatch({
        type: ActionTypes.SET_OPERATIONS,
        payload: operations
      });

      return operations;
    } catch (error) {
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: `Ошибка загрузки операций: ${error.message}`
      });
      throw error;
    } finally {
      setLoading(false, { operations: false });
    }
  };

  // Функция для загрузки серверов
  const loadServers = useCallback(async () => {
    try {
      setLoading(true, { servers: true });
      const servers = await apiService.getServers();
      dispatch({
        type: ActionTypes.SET_SERVERS,
        payload: servers
      });
      return servers;
    } catch (error) {
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: `Ошибка загрузки серверов: ${error.message}`
      });
      throw error;
    } finally {
      setLoading(false, { servers: false });
    }
  }, [setLoading, dispatch]); // Добавляем зависимости

  // Функция для покупки ключа
  const purchaseKey = async (purchaseData) => {
    try {
      setLoading(true, { purchase: true });
      
      const result = await apiService.purchaseKey(purchaseData);
      
      // Добавляем новую операцию в список
      if (result.operation) {
        dispatch({
          type: ActionTypes.ADD_OPERATION,
          payload: result.operation
        });
      }

      return result;
    } catch (error) {
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: `Ошибка покупки ключа: ${error.message}`
      });
      throw error;
    } finally {
      setLoading(false, { purchase: false });
    }
  };

  // Функция для создания VPN подписки
  const createSubscription = async (subscriptionData) => {
    try {
      setLoading(true, { purchase: true });
      
      const result = await apiService.createSubscription(subscriptionData);
      
      // Добавляем новую подписку в список
      if (result.subscription) {
        dispatch({
          type: ActionTypes.ADD_SUBSCRIPTION,
          payload: result.subscription
        });

        // Успешная вибрация
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.impactOccurred('heavy');
        }

        // Показываем popup об успешном создании
        if (window.Telegram?.WebApp?.showPopup) {
          window.Telegram.WebApp.showPopup({
            title: translate('popup_success_title', state.language),
            message: translate('popup_new_key', state.language),
            buttons: [{ type: 'ok' }]
          });
        }
      }

      return result;
    } catch (error) {
      console.error('Ошибка создания подписки:', error);
      
      // Показываем popup с ошибкой
      if (window.Telegram?.WebApp?.showPopup) {
        window.Telegram.WebApp.showPopup({
          title: translate('popup_error_title', state.language),
          message: `${translate('popup_purchase_error', state.language)} ${error.message}`,
          buttons: [{ type: 'ok' }]
        });
      }
      
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: `Ошибка создания подписки: ${error.message}`
      });
      throw error;
    } finally {
      setLoading(false, { purchase: false });
    }
  };

  // Функция для создания платежа через ЮКасса
  const createPayment = async (paymentData) => {
    try {
      setLoading(true, { purchase: true });
      
      const result = await apiService.createPayment(paymentData);
      
      if (result.confirmation_url) {
        // Успешная вибрация
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
        }

        // Открываем URL для оплаты
        if (window.Telegram?.WebApp?.openLink) {
          window.Telegram.WebApp.openLink(result.confirmation_url);
        } else {
          // Fallback для обычного браузера
          window.open(result.confirmation_url, '_blank');
        }
      } else if (result.amount === 0) {
        // Подписка активирована за бонусы
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.impactOccurred('heavy');
        }

        // Показываем popup об успешном создании
        if (window.Telegram?.WebApp?.showPopup) {
          window.Telegram.WebApp.showPopup({
            title: translate('popup_success_title', state.language),
            message: translate('popup_bonus_activated', state.language),
            buttons: [{ type: 'ok' }]
          });
        }

        // Обновляем данные пользователя
        if (state.user) {
          await loadUserSubscriptions(state.user.user_id);
        }
      }

      return result;
    } catch (error) {
      console.error('Ошибка создания платежа:', error);
      
      // Показываем popup с ошибкой
      if (window.Telegram?.WebApp?.showPopup) {
        window.Telegram.WebApp.showPopup({
          title: translate('popup_error_title', state.language),
          message: `${translate('popup_payment_error', state.language)} ${error.message}`,
          buttons: [{ type: 'ok' }]
        });
      }
      
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: `Ошибка создания платежа: ${error.message}`
      });
      throw error;
    } finally {
      setLoading(false, { purchase: false });
    }
  };

  // Функция для удаления подписки
  const deleteSubscription = async (subscriptionId) => {
    try {
      if (!state.user) {
        throw new Error('Пользователь не авторизован');
      }

      setLoading(true, { purchase: true });
      
      const deleteData = {
        subscription_id: subscriptionId,
        user_id: state.user.user_id
      };
      
      await apiService.deleteSubscription(deleteData);
      
      // Удаляем из контекста
      dispatch({
        type: ActionTypes.DELETE_SUBSCRIPTION,
        payload: subscriptionId
      });

      // Показываем popup об успешном удалении
      if (window.Telegram?.WebApp?.showPopup) {
        window.Telegram.WebApp.showPopup({
          title: translate('popup_success_title', state.language),
          message: translate('popup_subscription_deleted', state.language),
          buttons: [{ type: 'ok' }]
        });
      }
      
    } catch (error) {
      console.error('Ошибка удаления подписки:', error);
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: `Ошибка удаления подписки: ${error.message}`
      });
      throw error;
    } finally {
      setLoading(false, { purchase: false });
    }
  };

  // Функция для обновления темы
  const setTheme = (theme) => {
    dispatch({
      type: ActionTypes.SET_THEME,
      payload: theme
    });
  };

  // Функция для обновления темы пользователя с сохранением на сервере
  const updateUserTheme = async (theme) => {
    try {
      // Сначала обновляем тему локально для быстрого отклика UI
      setTheme(theme);
      
      // Затем отправляем запрос на сервер для сохранения
      if (state.user && state.user.id) {
        await apiService.updateUserTheme(state.user.id, theme);
      } else {
        console.error('Пользователь не найден');
      }
    } catch (error) {
      console.error('Ошибка обновления темы на сервере:', error);
      // В случае ошибки можно показать уведомление, но не откатывать тему локально
      // так как пользователь уже видит новую тему
    }
  };

  // Функция для обновления языка
  const setLanguage = (lang) => {
    dispatch({
      type: ActionTypes.SET_LANGUAGE,
      payload: lang
    });
  };

  // Функция для обновления языка пользователя с сохранением на сервере
  const updateUserLanguage = async (lang) => {
    try {
      // Сначала обновляем язык локально для быстрого отклика UI
      setLanguage(lang);
      
      // Затем отправляем запрос на сервер для сохранения
      if (state.user && state.user.id) {
        await apiService.updateUserLanguage(state.user.id, lang);
      } else {
        console.error('Пользователь не найден');
      }
    } catch (error) {
      console.error('Ошибка обновления языка на сервере:', error);
    }
  };

  // Функция локализации (краткий алиас)
  const t = useCallback((key, params = {}) => {
    return translate(key, state.language, params);
  }, [state.language]);

  // Функция для установки Telegram данных
  const setTelegramData = (isAvailable, themeParams = {}) => {
    dispatch({
      type: ActionTypes.SET_TELEGRAM_AVAILABLE,
      payload: isAvailable
    });
    
    dispatch({
      type: ActionTypes.SET_TELEGRAM_THEME,
      payload: themeParams
    });
  };

  // Функция для очистки ошибки
  const clearError = () => {
    dispatch({
      type: ActionTypes.CLEAR_ERROR
    });
  };

  // Проверяем есть ли активная загрузка
  const hasActiveLoading = () => {
    return state.isLoading || Object.values(state.loadingStates).some(Boolean);
  };

  // Значение контекста
  const contextValue = {
    // Состояние
    ...state,
    hasActiveLoading: hasActiveLoading(),
    
    // Действия
    setLoading,
    validateAndSetUser,
    loadUserSubscriptions,
    loadUserOperations,
    loadServers,
    purchaseKey,
    createSubscription,
    createPayment,
    deleteSubscription,
    setTheme,
    updateUserTheme,
    setLanguage,
    updateUserLanguage,
    t, // Функция локализации
    setTelegramData,
    clearError,
    
    // Прямые dispatch действия для более сложных случаев
    dispatch,
    ActionTypes
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Хук для использования контекста
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp должен использоваться внутри AppProvider');
  }
  return context;
};

export default AppContext; 